import _ from "lodash";

import API from "./API";
import BaseService from "./BaseService";

/**
 * @typedef {import("@/stores/PreviewStore").LambdaConfig} LambdaConfig
 * @typedef {import("@/hooks/query/useSite").SiteState} SiteState
 * @typedef {import("@/stores/DesignExportStore").DesignExport} DesignExport
 * @typedef {import("../constants/RendererConstants.mjs").RendererTypeValue} RendererTypeValue
 */

/**
 * Flat flag map describing whether a design qualifies for the one-way v2 renderer lock.
 * `eligible` is server-derived from `!isLocked && !isLibrary && hasV2Publication`;
 * `hasV2Preview` is informational (publish bypasses preview).
 *
 * @typedef {Object} RendererLockEligibility
 * @prop {boolean} eligible
 * @prop {boolean} isLocked
 * @prop {boolean} isLibrary
 * @prop {boolean} hasV2Publication
 * @prop {boolean} hasV2Preview
 */

/**
 * Row from `design_publications`. Returned as the `publication` field on the
 * publishSite / publishSiteV2 response body.
 *
 * @typedef {Object} Publication
 * @prop {number} pub_id
 * @prop {number} site_design_id
 * @prop {string} type
 * @prop {string} pub_key
 * @prop {string} pub_date
 * @prop {number} user_id
 * @prop {string | null} name
 * @prop {number} status
 * @prop {string} design_type
 * @prop {0 | 1} deleted
 * @prop {string | null} meta
 * @prop {number} model_version
 * @prop {string | null} page_id
 * @prop {string | null} block_id
 * @prop {number | null} export_id
 * @prop {0 | 1} has_preview_imgs
 * @prop {RendererTypeValue} renderer
 * @prop {string} renderer_version
 */

/**
 * Row from the platform-api `design_exports` table (DesignExportDAO.getByDesign).
 * Datetime columns arrive as ISO strings after JSON serialization.
 * @typedef {Object} DesignExportRecord
 * @prop {number} export_id
 * @prop {string} key
 * @prop {number} pub_id
 * @prop {number} site_design_id
 * @prop {string | null} name
 * @prop {string | null} desc
 * @prop {number} owner_id
 * @prop {DesignExport["designType"]} design_type
 * @prop {string} create_date
 * @prop {0 | 1} deleted
 * @prop {0 | 1} status
 * @prop {string | null} status_date
 * @prop {number | null} model_version
 * @prop {string | null} page_id
 * @prop {string | null} block_id
 * @prop {string} last_updated
 * 
 * 
 * @typedef {(
 *   | { locked: true, renderer: RendererTypeValue, canToggle: boolean }
 *   | { locked: false, eligibility: RendererLockEligibility }
 * )} LockResult
 */

class SiteService extends BaseService {
  constructor() {
    super();
  }

  loadSite({ site_design_id }) {
    return API.get(`sites/${site_design_id}`, null, this.getToken());
  }

  migrateDesign({ requestedVersion, key, migrationType, pubKey, force }) {
    return API.post(
      `designs/${key}/migrate`,
      { v: requestedVersion, migrationType, pubKey, force },
      this.getToken(),
    );
  }

  getSiteBackups(urlInfo, data) {
    return API.get(
      "user/" + urlInfo.user_id + "/sites/" + urlInfo.site_id + "/backups",
      data,
      this.getToken(),
    );
  }

  restoreSite(urlInfo, data) {
    return API.post(
      "user/" + urlInfo.user_id + "/sites/" + urlInfo.site_id + "/restore",
      data,
      this.getToken(),
    );
  }

  loadPage(data) {
    const { designId, pageId } = data;
    const url = `${designId}/pages/${pageId}.json`;

    return API.getDesignJSON(url);
  }

  updateSite(data) {
    return API.post(
      "sites/" + data.site_design_id,
      { data: data },
      this.getToken(),
    );
  }

  /**
   * Publishes a v1 site
   * @param {SiteState} data
   * @returns {Promise<{ publication: Publication }>}
   */
  publishSite(data) {
    return API.post(
      "sites/" + data.site_design_id + "/publish",
      {},
      this.getToken(),
    );
  }

  /**
   * Publishes a v2 site
   * @param {SiteState} data
   * @returns {Promise<{ publication: Publication }>}
   */
  publishSiteV2(data) {
    return API.post(
      "sites/" + data.site_design_id + "/publishV2",
      {},
      this.getToken(),
    );
  }

  /**
   * Publishes a v1 blog
   * @param {SiteState} data
   * @returns {Promise<any>}
   */
  publishBlog(data) {
    return API.post(
      "sites/" + data.site_design_id + "/publishBlog",
      {},
      this.getToken(),
    );
  }

  /**
   * Publishes a v2 blog
   * @param {SiteState} data
   * @returns {Promise<any>}
   */
  publishBlogV2(data) {
    return API.post(
      "sites/" + data.site_design_id + "/publishBlogV2",
      {},
      this.getToken(),
    );
  }

  removeSite(data) {
    return API.post(
      `designs/delete`,
      { designIds: [data.site_design_id] },
      this.getToken(),
    );
  }

  /**
   * Standard preview generation endpoint
   * @param {{ site_design_id: string }} data
   * @param {LambdaConfig} lambdaConfig
   * @returns
   */
  generatePreview(data, lambdaConfig) {
    const payload = {};
    if (lambdaConfig) {
      payload.lambdaConfig = lambdaConfig;
    }

    return API.post(
      `sites/${data.site_design_id}/generatePreview`,
      payload,
      this.getToken(),
    );
  }

  generateBlogPreview({ designId, blogId }) {
    return API.post(
      `designs/${designId}/generateBlogPreview`,
      { blogId },
      this.getToken(),
    );
  }

  /**
   * Protorenderer-based blog preview. Drop-in replacement for
   * generateBlogPreview: identical auth, body, and { blogPreviewUrl } response;
   * only the endpoint path differs.
   * @param {{ designId: string, blogId?: number }} args
   * @returns {Promise<{ blogPreviewUrl: string }>}
   */
  generateBlogPreviewV2({ designId, blogId }) {
    return API.post(
      `designs/${designId}/generateBlogPreviewV2`,
      { blogId },
      this.getToken(),
    );
  }

  /**
   * Reads a design's publish renderer opt-in.
   * @param {number} designId - site design id
   * @returns {Promise<{ renderer: RendererTypeValue, canToggle: boolean }>}
   */
  getRenderer(designId) {
    return API.get(`designs/${designId}/renderer`, null, this.getToken());
  }

  /**
   * Sets a design's publication renderer opt-in.
   * @param {number} designId - site design id
   * @param {RendererTypeValue} renderer
   * @returns {Promise<{ renderer: RendererTypeValue, canToggle: boolean }>}
   */
  updateRenderer(designId, renderer) {
    return API.patch(
      `designs/${designId}/renderer`,
      { renderer },
      this.getToken(),
    );
  }

  /**
   * Reads whether a design is eligible for the one-way lock into the v2 publish engine.
   * Advisory only — the lock endpoint re-runs the check server-side.
   * @param {number} designId - site design id
   * @returns {Promise<RendererLockEligibility>}
   */
  getRendererLockEligibility(designId) {
    return API.get(
      `designs/${designId}/renderer/lock-eligibility`,
      null,
      this.getToken(),
    );
  }

  /**
   * Attempts the one-way lock into the v2 publish engine. Always resolves with a
   * discriminated variant — `{ locked: true, ... }` if the lock succeeded (and
   * created the v1 backup design), `{ locked: false, eligibility }` if the design
   * became ineligible between the pre-flight check and the lock attempt.
   *
   * @param {number} designId - site design id
   * @returns {Promise<LockResult>}
   */
  lockRenderer(designId) {
    return API.post(`designs/${designId}/renderer/lock`, {}, this.getToken());
  }

  /**
   * Export v1 design
   * @param {string} siteId
   * @param {Pick<DesignExport, "blockId" | "pageId" | "desc" | "name" | "designType" | "exportId">} data
   * @returns {Promise<void>}
   */
  exportDesign(siteId, data) {
    return API.post("sites/" + siteId + "/export", data, this.getToken());
  }

  /**
   * Export v2 design
   * @param {string} siteId
   * @param {Pick<DesignExport, "blockId" | "pageId" | "desc" | "name" | "designType" | "exportId">} data
   * @returns {Promise<void>}
   */
  exportDesignV2(siteId, data) {
    return API.post("sites/" + siteId + "/exportV2", data, this.getToken());
  }

  /**
   *
   * @param {string} siteId
   * @param {Pick<DesignExport, "designType" | "pageId" | "blockId">} queryData
   * @returns {Promise<{status: number, designExports: DesignExportRecord[]}>}
   */
  getDesignExports(siteId, queryData) {
    var query = _.pick(queryData, ["designType", "pageId", "blockId"]);

    return API.get("sites/" + siteId + "/exports", query, this.getToken());
  }
}

export default new SiteService();
